import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";

import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import MarketingLayout from "@/components/layouts/MarketingLayout";
import DashboardLayout from "@/components/layouts/DashboardLayout";

import HomePage from "@/pages/HomePage";
import ServicesPage from "@/pages/ServicesPage";
import CoveragePage from "@/pages/CoveragePage";
import AboutPage from "@/pages/AboutPage";
import ContactPage from "@/pages/ContactPage";
import QuotePage from "@/pages/QuotePage";
import LoginPage from "@/pages/LoginPage";

import PortalOverview from "@/pages/portal/PortalOverview";
import PortalWorkOrders from "@/pages/portal/PortalWorkOrders";
import PortalNewWorkOrder from "@/pages/portal/PortalNewWorkOrder";
import PortalWorkOrderDetail from "@/pages/portal/PortalWorkOrderDetail";
import PortalInvoices from "@/pages/portal/PortalInvoices";

import AdminOverview from "@/pages/admin/AdminOverview";
import AdminWorkOrders from "@/pages/admin/AdminWorkOrders";
import AdminWorkOrderDetail from "@/pages/admin/AdminWorkOrderDetail";
import AdminClients from "@/pages/admin/AdminClients";
import AdminContacts from "@/pages/admin/AdminContacts";
import AdminQuotes from "@/pages/admin/AdminQuotes";
import AdminInvoices from "@/pages/admin/AdminInvoices";
import AdminIntegrations from "@/pages/admin/AdminIntegrations";

function AdminGuard({ children }) {
  const { user } = useAuth();
  if (user && user.role !== "admin") return <Navigate to="/portal" replace />;
  return children;
}

function ClientGuard({ children }) {
  const { user } = useAuth();
  if (user && user.role === "admin") return <Navigate to="/admin" replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="bottom-right" richColors closeButton />
        <Routes>
          {/* Marketing */}
          <Route element={<MarketingLayout />}>
            <Route path="/" element={<HomePage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/coverage" element={<CoveragePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/quote" element={<QuotePage />} />
          </Route>

          {/* Auth */}
          <Route path="/login" element={<LoginPage />} />

          {/* Client portal */}
          <Route
            element={
              <ProtectedRoute>
                <ClientGuard>
                  <DashboardLayout variant="client" />
                </ClientGuard>
              </ProtectedRoute>
            }
          >
            <Route path="/portal" element={<PortalOverview />} />
            <Route path="/portal/work-orders" element={<PortalWorkOrders />} />
            <Route path="/portal/work-orders/new" element={<PortalNewWorkOrder />} />
            <Route path="/portal/work-orders/:id" element={<PortalWorkOrderDetail />} />
            <Route path="/portal/invoices" element={<PortalInvoices />} />
          </Route>

          {/* Admin */}
          <Route
            element={
              <ProtectedRoute role="admin">
                <AdminGuard>
                  <DashboardLayout variant="admin" />
                </AdminGuard>
              </ProtectedRoute>
            }
          >
            <Route path="/admin" element={<AdminOverview />} />
            <Route path="/admin/work-orders" element={<AdminWorkOrders />} />
            <Route path="/admin/work-orders/:id" element={<AdminWorkOrderDetail />} />
            <Route path="/admin/clients" element={<AdminClients />} />
            <Route path="/admin/contacts" element={<AdminContacts />} />
            <Route path="/admin/quotes" element={<AdminQuotes />} />
            <Route path="/admin/invoices" element={<AdminInvoices />} />
            <Route path="/admin/integrations" element={<AdminIntegrations />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
